class CountdownGrid {
  constructor(section) {
    this.section = section;
    this.locale = section.dataset.countdownLocale || 'en-US';
    this.items = Array.from(section.querySelectorAll('[data-countup-value]'));
    this.hasAnimated = false;
    this.stagger = parseInt(section.dataset.countdownStagger || '300', 10);

    if (!this.items.length) return;

    this.initObserver();
  }

  initObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.animateAll();
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -5%',
      }
    );

    observer.observe(this.section);

    if (!this.hasAnimated && this.isInViewport()) {
      this.hasAnimated = true;
      this.animateAll();
      observer.disconnect();
    }
  }

  isInViewport() {
    const rect = this.section.getBoundingClientRect();
    return rect.top <= window.innerHeight && rect.bottom >= 0;
  }

  animateAll() {
    this.items.forEach((item, index) => {
      const rawValue = (item.dataset.countupValue || '').trim();

      if (!this.isNumericValue(rawValue)) {
        item.classList.remove('is-flickering');
        item.textContent = rawValue;
        return;
      }

      const normalized = rawValue.replace(/[\s,]/g, '');
      const decimals = this.getDecimalPlaces(normalized);
      const target = parseFloat(normalized);
      const baseDuration = parseInt(item.dataset.countupDuration, 10) || 2200;
      const duration = baseDuration + index * Math.max(this.stagger, 0);

      this.animateItem(item, target, duration, decimals);
    });
  }

  animateItem(item, target, duration, decimals = 0) {
    const formatter = new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    const start = performance.now();
    const flickerWindow = Math.min(400, duration * 0.3);

    item.textContent = formatter.format(0);

    const step = (timestamp) => {
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = this.easeOutCubic(progress);
      const currentValue = target * easedProgress;
      const formattedValue = formatter.format(Number(currentValue.toFixed(decimals)));

      if (elapsed < flickerWindow) {
        item.classList.add('is-flickering');
        item.textContent = this.getRandomNumber(target, decimals, formatter);
      } else {
        item.classList.remove('is-flickering');
        item.textContent = formattedValue;
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        item.classList.remove('is-flickering');
        item.textContent = formatter.format(target);
      }
    };

    requestAnimationFrame(step);
  }

  getRandomNumber(target, decimals, formatter) {
    const max = Math.max(target, 1);
    const randomValue = Math.random() * max;
    const value = decimals > 0 ? Number(randomValue.toFixed(decimals)) : Math.floor(randomValue);
    return formatter.format(value);
  }

  isNumericValue(value) {
    if (!value) return false;
    const numericPattern = /^[0-9\s,\.]+$/;
    if (!numericPattern.test(value)) return false;

    const normalized = value.replace(/[\s,]/g, '');
    return normalized !== '' && /^\d+(\.\d+)?$/.test(normalized);
  }

  getDecimalPlaces(value) {
    const parts = value.split('.');
    return parts.length > 1 ? parts[1].length : 0;
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
}

const initCountdownGrid = () => {
  const sections = document.querySelectorAll('[data-countdown-grid]');
  sections.forEach((section) => new CountdownGrid(section));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCountdownGrid);
} else {
  initCountdownGrid();
}
